import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, KeyRound, School } from "lucide-react";

import ActionToast from "../../../core/components/ActionToast";
import GlobalLoadingPage from "../../../core/components/GlobalLoadingPage";
import eduLogo from "../../../assets/logos/Oikos_Student_Devices_Logo.png";
import devicesLoginBackground from "../../../assets/backgrounds/Devices Login Screen.png";
import defaultEduDeviceBackground from "../../../assets/backgrounds/Oikos-EDU-Default-Background.svg";
import {
  getEduTeacherPortalSchool,
  loginEduTeacherPortal,
} from "../services/studentDeviceService";

const DEFAULT_BACKGROUND = {
  imageUrl: defaultEduDeviceBackground,
  color: "#f8f9fb",
};
const DEFAULT_FIND_ORG_BACKGROUND = {
  imageUrl: devicesLoginBackground,
  color: "#f8f9fb",
};
const DEVICE_BRAND_COLOR = "#D3412A";
const MIN_TEACHER_LOADING_MS = 0;

function waitForMinimumLoading(startedAt, minimumMs = MIN_TEACHER_LOADING_MS) {
  const remaining = minimumMs - (Date.now() - startedAt);
  return remaining > 0 ? new Promise((resolve) => window.setTimeout(resolve, remaining)) : Promise.resolve();
}

function getEduAppOrigin() {
  if (typeof window === "undefined") return "https://oikosedu.app";

  const hostname = String(window.location.hostname || "").toLowerCase();
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".local")) {
    return window.location.origin;
  }

  return "https://oikosedu.app";
}

function getLoginBackground(account = {}, defaultBackground = DEFAULT_BACKGROUND) {
  if (account.campusLoginBackground?.imageUrl) {
    return {
      imageUrl: account.campusLoginBackground.imageUrl,
      color: account.campusLoginBackground.color || defaultBackground.color || DEFAULT_BACKGROUND.color,
    };
  }

  const deviceBackground = account.deviceBackground || defaultBackground;
  const loginBackground = account.deviceLoginBackground || {};
  if (loginBackground.useDeviceBackground !== false) {
    return {
      imageUrl: deviceBackground.imageUrl || defaultBackground.imageUrl || DEFAULT_BACKGROUND.imageUrl,
      color: deviceBackground.color || defaultBackground.color || DEFAULT_BACKGROUND.color,
    };
  }

  return {
    imageUrl: loginBackground.imageUrl || "",
    color: loginBackground.color || deviceBackground.color || defaultBackground.color || DEFAULT_BACKGROUND.color,
  };
}

function getBackgroundStyle(background = {}) {
  return {
    backgroundColor: background.color || DEFAULT_BACKGROUND.color,
    backgroundImage: "none",
  };
}

function getSchoolLink(account = {}, loginPath = "/devices/teacher") {
  const code = String(account.deviceCode || "").trim();
  return code ? `${getEduAppOrigin()}${loginPath}/${encodeURIComponent(code)}` : "";
}

export default function EduTeacherLoginPage({
  destinationPath = "/devices/teacher",
  loginPath = "/devices/teacher",
  schoolCodeOverride = "",
  titleFallback = "Find Organization",
  schoolCodeLabel = "Organization or School Code",
  lookupButtonLabel = "Find Organization",
  lookupLoadingLabel = "Finding Organization...",
  standaloneHallPass,
  showStandaloneStudentDevices = true,
  fallbackLogo = eduLogo,
  fallbackLogoAlt = "Oikos Managed Devices",
  cornerLogo = null,
  cornerLogoAlt = "",
  cornerLogoStyle = null,
  showPlatformLogoOnOrgBackground = false,
  coverAppChrome = false,
  hidePanelLogo = false,
  useAccountLogo = true,
  backgroundOverride = null,
  defaultBackground = DEFAULT_BACKGROUND,
  themeColorOverride = "",
  logoFrameStyle = null,
  logoImageStyle = null,
  panelIconSrc = "",
  panelIconAlt = "",
} = {}) {
  const { schoolCode: routeSchoolCode = "" } = useParams();
  const initialSchoolCode = schoolCodeOverride || routeSchoolCode;
  const navigate = useNavigate();
  const [schoolCode, setSchoolCode] = useState(initialSchoolCode.toUpperCase());
  const [school, setSchool] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(initialSchoolCode ? "lookup" : "");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const account = school?.account || null;
  const canUseStandaloneStudentDevices = standaloneHallPass ?? showStandaloneStudentDevices;
  const themeColor = themeColorOverride || DEVICE_BRAND_COLOR;
  const loginBackground = useMemo(
    () => {
      if (backgroundOverride) return backgroundOverride;
      if (account) return getLoginBackground(account, defaultBackground);
      return defaultBackground === DEFAULT_BACKGROUND ? DEFAULT_FIND_ORG_BACKGROUND : defaultBackground;
    },
    [account, backgroundOverride, defaultBackground]
  );
  const schoolLink = useMemo(() => getSchoolLink(account || {}, loginPath), [account, loginPath]);
  const platformCornerLogo = cornerLogo || (showPlatformLogoOnOrgBackground && account ? fallbackLogo : null);

  useEffect(() => {
    let isMounted = true;
    const cleanCode = String(initialSchoolCode || "").trim().toUpperCase();
    if (!cleanCode) return undefined;

    const loadingStartedAt = Date.now();
    setSchoolCode(cleanCode);
    setLoading("lookup");
    setError("");
    getEduTeacherPortalSchool(cleanCode)
      .then((nextSchool) => {
        if (isMounted) setSchool(nextSchool);
      })
      .catch((lookupError) => {
        if (isMounted) setError(lookupError?.message || "School code was not found.");
      })
      .finally(async () => {
        await waitForMinimumLoading(loadingStartedAt);
        if (isMounted) setLoading("");
      });

    return () => {
      isMounted = false;
    };
  }, [initialSchoolCode]);

  async function handleLookup(event) {
    event.preventDefault();
    const loadingStartedAt = Date.now();
    setLoading("lookup");
    setError("");
    setNotice("");
    try {
      const nextSchool = await getEduTeacherPortalSchool(schoolCode);
      await waitForMinimumLoading(loadingStartedAt);
      setSchool(nextSchool);
      const cleanCode = nextSchool?.account?.deviceCode || schoolCode;
      window.history.replaceState(null, "", `${loginPath}/${encodeURIComponent(cleanCode)}`);
    } catch (lookupError) {
      await waitForMinimumLoading(loadingStartedAt);
      setSchool(null);
      setError(lookupError?.message || "School code was not found.");
    } finally {
      setLoading("");
    }
  }

  async function handleLogin(event) {
    event.preventDefault();
    const loadingStartedAt = Date.now();
    setLoading("login");
    setError("");
    setNotice("");
    try {
      await loginEduTeacherPortal({
        schoolCode: account?.deviceCode || schoolCode,
        username,
        password,
      });
      const nextDestination =
        typeof destinationPath === "function"
          ? destinationPath(account || {}, schoolCode)
          : destinationPath;
      await waitForMinimumLoading(loadingStartedAt);
      navigate(nextDestination || "/devices/teacher", { replace: true });
    } catch (loginError) {
      await waitForMinimumLoading(loadingStartedAt);
      setError(loginError?.message || "Could not sign in.");
    } finally {
      setLoading("");
    }
  }

  return (
    <main
      style={{
        ...styles.page,
        ...getBackgroundStyle(loginBackground),
        "--teacher-color": themeColor,
        ...(coverAppChrome ? styles.coverAppChrome : {}),
      }}
    >
      {loading ? (
        <GlobalLoadingPage
          title={loading === "login" ? "Loading Teacher Portal" : lookupLoadingLabel}
          detail={loading === "login" ? "Checking your teacher sign in." : "Looking up your organization."}
          modeOverride="devices"
          primaryColorOverride={themeColor}
          iconOverride={panelIconSrc}
          iconFallbackOverride={panelIconSrc}
          labelOverride={panelIconAlt || "Managed Devices"}
        />
      ) : null}
      {loginBackground.imageUrl ? (
        <img
          aria-hidden="true"
          alt=""
          src={loginBackground.imageUrl}
          style={styles.loginBackgroundImage}
        />
      ) : null}
      {platformCornerLogo ? (
        <div style={{ ...styles.cornerLogo, ...(cornerLogoStyle || {}) }}>
          <img src={platformCornerLogo} alt={cornerLogoAlt || fallbackLogoAlt} style={styles.cornerLogoImage} />
        </div>
      ) : null}
      <div style={styles.shell}>
        <form style={styles.panel} onSubmit={account ? handleLogin : handleLookup}>
          {panelIconSrc ? (
            <div style={styles.panelIconTile}>
              <img src={panelIconSrc} alt={panelIconAlt || fallbackLogoAlt} style={styles.panelIconImage} />
            </div>
          ) : null}
          {!hidePanelLogo ? (
            <div style={{ ...styles.logo, ...(logoFrameStyle || {}) }}>
              {useAccountLogo && account?.logoUrl ? (
                <img src={account.logoUrl} alt={account.name || "School"} style={styles.schoolLogo} />
              ) : (
                <img src={fallbackLogo} alt={fallbackLogoAlt} style={{ ...styles.logoImage, ...(logoImageStyle || {}) }} />
              )}
            </div>
          ) : null}

          <h1 style={styles.title}>{account?.name || titleFallback}</h1>

          {!account ? (
            <>
              <label style={styles.label}>
                {schoolCodeLabel}
                <input
                  style={styles.input}
                  value={schoolCode}
                  onChange={(event) => setSchoolCode(event.target.value.toUpperCase())}
                  autoComplete="organization"
                  autoFocus
                />
              </label>
              <button style={styles.loginButton} disabled={Boolean(loading)} type="submit">
                <School size={17} />
                {loading === "lookup" ? lookupLoadingLabel : lookupButtonLabel}
              </button>
              {canUseStandaloneStudentDevices ? (
                <button style={styles.standaloneButton} disabled={Boolean(loading)} type="button" onClick={() => navigate("/devices/student")}>
                  <KeyRound size={17} />
                  Use Standalone Managed Devices
                </button>
              ) : null}
            </>
          ) : (
            <>
              <label style={styles.label}>
                Username
                <input
                  style={styles.input}
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </label>
              <label style={styles.label}>
                Password
                <input
                  style={styles.input}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  type="password"
                />
              </label>
              {schoolLink ? (
                <div style={styles.linkBox}>
                  <span>School login link</span>
                  <a href={schoolLink} style={styles.schoolLink}>
                    {schoolLink}
                  </a>
                </div>
              ) : null}
              <button style={styles.loginButton} disabled={Boolean(loading)} type="submit">
                <KeyRound size={17} />
                {loading === "login" ? "Signing In..." : "Enter Teacher Portal"}
              </button>
              <button
                style={styles.changeSchoolButton}
                disabled={Boolean(loading)}
                onClick={() => {
                  setSchool(null);
                  setPassword("");
                  setNotice("");
                  setError("");
                  window.history.replaceState(null, "", loginPath);
                }}
                type="button"
              >
                <ArrowLeft size={16} />
                Change School
              </button>
            </>
          )}

          <ActionToast message={error} tone="error" onDismiss={() => setError("")} />
          <ActionToast message={!error ? notice : ""} tone="success" onDismiss={() => setNotice("")} />
        </form>
      </div>
    </main>
  );
}

const styles = {
  page: {
    alignItems: "center",
    backgroundAttachment: "fixed",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    boxSizing: "border-box",
    display: "grid",
    inset: 0,
    justifyItems: "center",
    minHeight: "100dvh",
    overflow: "auto",
    padding: 20,
    placeItems: "center",
    position: "fixed",
    width: "100vw",
  },
  coverAppChrome: {
    zIndex: 1000,
  },
  loginBackgroundImage: {
    height: "100%",
    inset: 0,
    objectFit: "cover",
    pointerEvents: "none",
    position: "fixed",
    width: "100%",
    zIndex: 0,
  },
  cornerLogo: {
    alignItems: "center",
    background: "rgba(255,255,255,0.78)",
    backdropFilter: "blur(18px) saturate(1.12)",
    WebkitBackdropFilter: "blur(18px) saturate(1.12)",
    border: "1px solid rgba(255,255,255,0.68)",
    borderRadius: 16,
    boxShadow: "0 18px 46px rgba(15,23,42,0.16)",
    boxSizing: "border-box",
    display: "flex",
    height: 74,
    justifyContent: "center",
    left: 24,
    padding: 10,
    pointerEvents: "none",
    position: "fixed",
    top: 20,
    width: 188,
    zIndex: 2,
  },
  cornerLogoImage: {
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
  },
  shell: {
    alignItems: "center",
    display: "grid",
    justifyItems: "center",
    maxWidth: 410,
    position: "relative",
    width: "100%",
    zIndex: 1,
  },
  panel: {
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(18px) saturate(1.12)",
    WebkitBackdropFilter: "blur(18px) saturate(1.12)",
    border: "1px solid rgba(255,255,255,0.56)",
    borderRadius: 24,
    boxShadow: "0 18px 46px rgba(15,23,42,0.14)",
    display: "grid",
    gap: 12,
    justifyItems: "stretch",
    padding: 22,
    position: "relative",
    width: "100%",
  },
  panelIconTile: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    height: 38,
    justifyContent: "center",
    left: 18,
    overflow: "hidden",
    position: "absolute",
    top: 18,
    width: 38,
  },
  panelIconImage: {
    height: "100%",
    objectFit: "contain",
    width: "100%",
  },
  logo: {
    alignItems: "center",
    display: "flex",
    height: 72,
    justifyContent: "center",
    justifySelf: "center",
    overflow: "hidden",
    width: 176,
  },
  logoImage: {
    height: "100%",
    objectFit: "contain",
    width: "100%",
  },
  schoolLogo: {
    maxHeight: 72,
    maxWidth: 176,
    objectFit: "contain",
  },
  title: {
    color: "#0f172a",
    fontSize: 28,
    margin: "4px 0 6px",
    textAlign: "center",
  },
  label: {
    color: "#475569",
    display: "grid",
    fontSize: 13,
    fontWeight: 800,
    gap: 6,
  },
  input: {
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(15,23,42,0.10)",
    borderRadius: 14,
    boxSizing: "border-box",
    color: "#0f172a",
    font: "inherit",
    minHeight: 44,
    padding: "10px 11px",
  },
  loginButton: {
    alignItems: "center",
    background: `var(--teacher-color, ${DEVICE_BRAND_COLOR})`,
    border: 0,
    borderRadius: 14,
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 46,
  },
  standaloneButton: {
    alignItems: "center",
    background: "rgba(211,65,42,0.08)",
    border: "1px solid rgba(211,65,42,0.16)",
    borderRadius: 14,
    color: `var(--teacher-color, ${DEVICE_BRAND_COLOR})`,
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 44,
  },
  resetButton: {
    alignItems: "center",
    background: "rgba(211,65,42,0.08)",
    border: "1px solid rgba(211,65,42,0.16)",
    borderRadius: 14,
    color: `var(--teacher-color, ${DEVICE_BRAND_COLOR})`,
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 44,
  },
  changeSchoolButton: {
    alignItems: "center",
    background: "transparent",
    border: 0,
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 36,
  },
  linkBox: {
    background: "rgba(255,255,255,0.56)",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 14,
    color: "#64748b",
    display: "grid",
    fontSize: 12,
    gap: 4,
    padding: 10,
  },
  schoolLink: {
    color: `var(--teacher-color, ${DEVICE_BRAND_COLOR})`,
    fontWeight: 900,
    overflowWrap: "anywhere",
    textDecoration: "none",
  },
  notice: {
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    borderRadius: 14,
    color: "#047857",
    padding: 10,
  },
  error: {
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    borderRadius: 14,
    color: "#be123c",
    padding: 10,
  },
};
